import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ContractService } from '@app/features/contracts/contract-service';
import { ContractListItem, ContractStatus } from '@app/features/contracts/contract.model';
import {
  CreateValidationRequestPayload,
  UpdateValidationRequestPayload,
  ValidationRequest,
  ValidationRequestType,
} from '@app/features/contracts/validation/validation-request.model';
import { UserRole } from '@app/features/users/user.model';
import { RUNTIME_CONFIG } from '@environments/runtime-config.token';
import { BehaviorSubject, catchError, map, Observable, of, switchMap } from 'rxjs';

export const ROLES_ALLOWED_TO_REQUEST_VALIDATION: Record<ValidationRequestType, UserRole[]> = {
  REX_VALIDATION: [
    'assistant-de-production',
    'responsable-exploitation',
    'directeur-agence',
    'directeur-production',
    'responsable-rh',
    'directeur-rh',
  ],
  DA_VALIDATION: [
    'responsable-exploitation',
    'directeur-agence',
    'directeur-production',
    'responsable-rh',
    'directeur-rh',
  ],
  DP_VALIDATION: ['directeur-agence', 'directeur-production', 'responsable-rh', 'directeur-rh'],
  RRH_VALIDATION: ['directeur-production', 'responsable-rh', 'directeur-rh'],
  DRH_VALIDATION: ['responsable-rh', 'directeur-rh'],
};

export const ROLES_ALLOWED_TO_BY_PASS_REQUESTED_TO: Record<ValidationRequestType, UserRole[]> = {
  REX_VALIDATION: ['directeur-agence', 'directeur-production', 'responsable-rh', 'directeur-rh'],
  DA_VALIDATION: ['directeur-production', 'responsable-rh', 'directeur-rh'],
  DP_VALIDATION: ['responsable-rh', 'directeur-rh'],
  RRH_VALIDATION: ['responsable-rh', 'directeur-rh'],
  DRH_VALIDATION: [],
};

export const REQUEST_VALIDATION_TO_ROLE: Record<ValidationRequestType, UserRole[]> = {
  REX_VALIDATION: ['responsable-exploitation', 'directeur-agence'],
  DA_VALIDATION: ['directeur-agence', 'directeur-production'],
  DP_VALIDATION: ['directeur-production', 'responsable-rh'],
  RRH_VALIDATION: ['responsable-rh', 'directeur-rh'],
  DRH_VALIDATION: ['directeur-rh'],
};

export const STATUS_ALLOWED_FOR_VALIDATION_ACTION: Record<ValidationRequestType, ContractStatus[]> =
  {
    REX_VALIDATION: ['REX_VALIDATING'],
    DA_VALIDATION: ['DA_VALIDATING'],
    DP_VALIDATION: ['DP_VALIDATING'],
    RRH_VALIDATION: ['RRH_VALIDATING'],
    DRH_VALIDATION: ['DRH_VALIDATING'],
  };

@Injectable({
  providedIn: 'root',
})
export class ValidationRequestService {
  private http = inject(HttpClient);
  private contractService = inject(ContractService);

  private url = `${inject(RUNTIME_CONFIG).apiUrl}/validation-requests`;
  private previewValidationRequestSubject = new BehaviorSubject<ContractListItem | null>(null);

  get previewValidationRequest$(): Observable<ContractListItem | null> {
    return this.previewValidationRequestSubject.asObservable();
  }

  private refreshContract(contractId: string): Observable<void> {
    return this.contractService.findOne(contractId).pipe(
      map(() => void 0),
      catchError((error) => {
        console.error('Contract refresh failed after validation request operation', {
          contractId,
          error,
        });
        return of(void 0);
      }),
    );
  }

  requestValidation(request: CreateValidationRequestPayload): Observable<ValidationRequest> {
    return this.http
      .post<ValidationRequest>(this.url, request)
      .pipe(
        switchMap((validationRequest) =>
          this.refreshContract(request.contractId).pipe(map(() => validationRequest)),
        ),
      );
  }

  acceptValidation(contractId: string, refreshContract = true): Observable<ValidationRequest> {
    const acceptPayload: UpdateValidationRequestPayload = {
      contractId,
      comment: null,
    };
    return this.http
      .post<ValidationRequest>(`${this.url}/accept`, acceptPayload)
      .pipe(
        switchMap((validationRequest) =>
          refreshContract
            ? this.refreshContract(contractId).pipe(map(() => validationRequest))
            : of(validationRequest),
        ),
      );
  }

  acceptRexValidationAndRequestDrhValidation(
    contractId: string,
    requestedTo: string,
    comment: string,
  ): Observable<ValidationRequest> {
    const acceptPayload: UpdateValidationRequestPayload = {
      contractId,
      comment: '',
    };
    const requestDrhValidationPayload: CreateValidationRequestPayload = {
      contractId,
      requestedTo,
      type: 'DRH_VALIDATION',
      comment,
    };
    return this.http
      .post<ValidationRequest>(`${this.url}/accept`, acceptPayload)
      .pipe(switchMap(() => this.requestValidation(requestDrhValidationPayload)));
  }

  refuseValidation(contractId: string, comment: string): Observable<ValidationRequest> {
    const payload: UpdateValidationRequestPayload = {
      contractId,
      comment,
    };
    return this.http
      .post<ValidationRequest>(`${this.url}/refuse`, payload)
      .pipe(
        switchMap((validationRequest) =>
          this.refreshContract(contractId).pipe(map(() => validationRequest)),
        ),
      );
  }

  cancelValidation(contractId: string, comment: string): Observable<ValidationRequest> {
    const payload: UpdateValidationRequestPayload = {
      contractId,
      comment,
    };
    return this.http
      .post<ValidationRequest>(`${this.url}/cancel`, payload)
      .pipe(
        switchMap((validationRequest) =>
          this.refreshContract(contractId).pipe(map(() => validationRequest)),
        ),
      );
  }

  acceptDrhValidation(contractId: string, comment: string): Observable<ValidationRequest> {
    const payload: UpdateValidationRequestPayload = {
      contractId,
      comment,
    };
    return this.http
      .post<ValidationRequest>(`${this.url}/accept`, payload)
      .pipe(
        switchMap((validationRequest) =>
          this.refreshContract(contractId).pipe(map(() => validationRequest)),
        ),
      );
  }

  preview(contract: ContractListItem) {
    this.previewValidationRequestSubject.next(contract);
  }

  canRequestValidationForState(
    contract: Pick<ContractListItem, 'status'>,
    type: ValidationRequestType,
  ): boolean {
    switch (type) {
      case 'REX_VALIDATION':
        return contract.status === 'DRAFT';
      case 'DA_VALIDATION':
        return contract.status === 'REX_VALIDATED';
      case 'DP_VALIDATION':
        return contract.status === 'DA_VALIDATED';
      case 'RRH_VALIDATION':
        return contract.status === 'DP_VALIDATED';
      case 'DRH_VALIDATION':
        return contract.status === 'RRH_VALIDATED';
    }
  }
}
